import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { User, onAuthStateChanged, updateProfile } from 'firebase/auth';
import { auth, db, rtdb, storage } from '@/lib/firebase';
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, query, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { ref, onDisconnect, set, serverTimestamp, onValue } from 'firebase/database';
import { useToast } from '@/components/ui/use-toast';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

interface UserData {
  id: string;
  name: string;
  displayName?: string;
  email: string;
  photoURL: string | null;
  coverURL: string | null;
  username: string;
  bio: string;
  location: string;
  website?: string;
  twitter?: string;
  instagram?: string;
  facebook?: string;
  linkedin?: string;
  github?: string;
  youtube?: string;
  tiktok?: string;
  createdAt: string;
  lastSeen: string;
  isOnline: boolean;
  // Admin and role fields
  isAdmin: boolean;
  isSeller: boolean;
  role: 'user' | 'admin' | 'seller' | 'superadmin';
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  userData: UserData | null;
  isOffline: boolean;
  updateUserData: (data: Partial<UserData>) => Promise<void>;
  uploadPhoto: (file: File, type: 'profile' | 'cover') => Promise<string>;
  retryConnection: () => Promise<void>;
}

type LoginGeoMetadata = {
  ip: string;
  city: string;
  region: string;
  country: string;
  countryCode: string;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  userData: null,
  isOffline: false,
  updateUserData: async () => {},
  uploadPhoto: async () => '',
  retryConnection: async () => {},
});

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const { toast } = useToast();
  const loggedSignInFingerprintsRef = useRef<Set<string>>(new Set());

  const isPermissionDeniedError = (error: unknown): boolean => {
    const code = String((error as any)?.code || '').toLowerCase();
    const message = String((error as any)?.message || '').toLowerCase();
    return code.includes('permission-denied') || message.includes('insufficient permissions');
  };

  const parseAuthMetadataTime = (value?: string | null): string => {
    if (!value) return new Date().toISOString();
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : new Date().toISOString();
  };

  const getOrCreateSessionId = (uid: string): string => {
    if (typeof window === 'undefined') {
      return `${uid}-${Date.now()}`;
    }

    const storageKey = `intouch:session-id:${uid}`;
    const existing = window.sessionStorage.getItem(storageKey);
    if (existing) return existing;

    const generated = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${uid}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    window.sessionStorage.setItem(storageKey, generated);
    return generated;
  };

  const getDeviceMetadata = () => {
    if (typeof navigator === 'undefined') {
      return {
        device: 'Unknown device',
        browser: 'Unknown browser',
        userAgent: 'Unknown user agent',
      };
    }

    const ua = navigator.userAgent;

    const browser = (() => {
      if (/Edg\//i.test(ua)) return 'Edge';
      if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) return 'Chrome';
      if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return 'Safari';
      if (/Firefox\//i.test(ua)) return 'Firefox';
      if (/OPR\//i.test(ua)) return 'Opera';
      return 'Unknown browser';
    })();

    const device = (() => {
      if (/iPhone/i.test(ua)) return 'iPhone';
      if (/iPad/i.test(ua)) return 'iPad';
      if (/Android/i.test(ua)) return 'Android device';
      if (/Macintosh|Mac OS X/i.test(ua)) return 'Mac';
      if (/Windows/i.test(ua)) return 'Windows device';
      if (/Linux/i.test(ua)) return 'Linux device';
      return 'Unknown device';
    })();

    return { device, browser, userAgent: ua };
  };

  const fetchJsonWithTimeout = async (url: string, timeoutMs = 3500): Promise<any | null> => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const resolveLoginGeoMetadata = async (): Promise<LoginGeoMetadata> => {
    const fallback: LoginGeoMetadata = {
      ip: 'Unknown',
      city: 'Unknown',
      region: 'Unknown',
      country: 'Unknown',
      countryCode: 'Unknown',
    };

    if (typeof window === 'undefined' || !navigator.onLine) {
      return fallback;
    }

    const ipApi = await fetchJsonWithTimeout('https://ipapi.co/json/');
    if (ipApi && !ipApi.error) {
      return {
        ip: String(ipApi.ip || 'Unknown'),
        city: String(ipApi.city || 'Unknown'),
        region: String(ipApi.region || 'Unknown'),
        country: String(ipApi.country_name || 'Unknown'),
        countryCode: String(ipApi.country_code || 'Unknown'),
      };
    }

    const ipWhoIs = await fetchJsonWithTimeout('https://ipwho.is/');
    if (ipWhoIs && ipWhoIs.success !== false) {
      return {
        ip: String(ipWhoIs.ip || 'Unknown'),
        city: String(ipWhoIs.city || 'Unknown'),
        region: String(ipWhoIs.region || 'Unknown'),
        country: String(ipWhoIs.country || 'Unknown'),
        countryCode: String(ipWhoIs.country_code || 'Unknown'),
      };
    }

    const ipInfo = await fetchJsonWithTimeout('https://ipinfo.io/json');
    if (ipInfo) {
      return {
        ip: String(ipInfo.ip || 'Unknown'),
        city: String(ipInfo.city || 'Unknown'),
        region: String(ipInfo.region || 'Unknown'),
        country: String(ipInfo.country || 'Unknown'),
        countryCode: String(ipInfo.country || 'Unknown'),
      };
    }

    return fallback;
  };

  const markSignInFingerprint = (uid: string, fingerprint: string) => {
    loggedSignInFingerprintsRef.current.add(fingerprint);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(`intouch:last-login-fingerprint:${uid}`, fingerprint);
    }
  };

  const hasSignInFingerprint = (uid: string, fingerprint: string): boolean => {
    if (loggedSignInFingerprintsRef.current.has(fingerprint)) {
      return true;
    }

    if (typeof window === 'undefined') {
      return false;
    }

    const previous = window.localStorage.getItem(`intouch:last-login-fingerprint:${uid}`);
    return previous === fingerprint;
  };

  const recordSuccessfulLogin = async (currentUser: User) => {
    const loginAtIso = parseAuthMetadataTime(currentUser.metadata?.lastSignInTime);
    const fingerprint = `${currentUser.uid}:${loginAtIso}`;
    if (hasSignInFingerprint(currentUser.uid, fingerprint)) {
      return;
    }

    try {
      const sessionId = getOrCreateSessionId(currentUser.uid);
      const [geo, deviceMeta] = await Promise.all([
        resolveLoginGeoMetadata(),
        Promise.resolve(getDeviceMetadata()),
      ]);

      const location = [geo.city, geo.region, geo.country]
        .map((segment) => segment.trim())
        .filter((segment) => segment.length > 0 && segment !== 'Unknown')
        .join(', ') || 'Unknown location';

      const nowIso = new Date().toISOString();

      await addDoc(collection(db, 'login_history'), {
        userId: currentUser.uid,
        email: currentUser.email || '',
        sessionId,
        action: 'Successful login',
        status: 'success',
        device: deviceMeta.device,
        browser: deviceMeta.browser,
        userAgent: deviceMeta.userAgent,
        ip: geo.ip,
        city: geo.city,
        region: geo.region,
        country: geo.country,
        countryCode: geo.countryCode,
        location,
        timestamp: loginAtIso,
        createdAt: nowIso,
      });

      const sessionsRef = collection(db, 'user_sessions');
      const existingSessions = await getDocs(query(sessionsRef, where('userId', '==', currentUser.uid)));
      await Promise.all(
        existingSessions.docs
          .filter((sessionDoc) => Boolean((sessionDoc.data() as any)?.current))
          .map((sessionDoc) => updateDoc(sessionDoc.ref, {
            current: false,
            updatedAt: nowIso,
          }))
      );

      await setDoc(doc(db, 'user_sessions', `${currentUser.uid}_${sessionId}`), {
        userId: currentUser.uid,
        email: currentUser.email || '',
        sessionId,
        current: true,
        device: deviceMeta.device,
        browser: deviceMeta.browser,
        userAgent: deviceMeta.userAgent,
        ip: geo.ip,
        city: geo.city,
        region: geo.region,
        country: geo.country,
        countryCode: geo.countryCode,
        location,
        lastSignInAt: loginAtIso,
        lastActive: nowIso,
        updatedAt: nowIso,
        createdAt: nowIso,
      }, { merge: true });

      markSignInFingerprint(currentUser.uid, fingerprint);
    } catch (error) {
      console.error('Error recording successful login:', error);
    }
  };

  // Track mounted state to prevent setting state after unmount.
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Presence is non-blocking: it should not delay initial app render.
  const setupPresence = async (currentUser: User) => {
    const userStatusRef = ref(rtdb, `status/${currentUser.uid}`);
    const userFirestoreRef = doc(db, 'users', currentUser.uid);
    let connectedUnsubscribe: undefined | (() => void);
    let presenceInterval: undefined | ReturnType<typeof setInterval>;
    let cancelled = false;
    
    const userStatusObject = {
      isOnline: true,
      lastSeen: serverTimestamp(),
    };

    try {
      if (cancelled) return () => {};
      // Set online in Realtime Database
      await set(userStatusRef, userStatusObject);
      if (cancelled) return () => {};
      
      // Update Firestore with online status
      await updateDoc(userFirestoreRef, {
        isOnline: true,
        lastSeen: new Date().toISOString()
      }).catch(() => {
        // If document doesn't exist, it will be created during setup
        console.log('Will update online status after user document is created');
      });
      
      // Set up disconnect handler for Realtime Database
      await onDisconnect(userStatusRef).update({
        isOnline: false,
        lastSeen: serverTimestamp(),
      });
      
      // Update Firestore on disconnect (will be handled by onDisconnect)
      const connectedRef = ref(rtdb, '.info/connected');
      connectedUnsubscribe = onValue(connectedRef, async (snapshot) => {
        if (cancelled) return;
        if (snapshot.val() === false) {
          // Connection lost
          try {
            await updateDoc(userFirestoreRef, {
              isOnline: false,
              lastSeen: new Date().toISOString()
            });
          } catch (error) {
            if (!isPermissionDeniedError(error)) {
              console.error('Error updating offline status in Firestore:', error);
            }
          }
        } else {
          // Connection established
          try {
            await updateDoc(userFirestoreRef, {
              isOnline: true,
              lastSeen: new Date().toISOString()
            });
          } catch (error) {
            if (!isPermissionDeniedError(error)) {
              console.error('Error updating online status in Firestore:', error);
            }
          }
        }
      });
      
      // Update last seen every 60 seconds while online
      presenceInterval = setInterval(async () => {
        if (cancelled) return;
        try {
          await set(userStatusRef, {
            isOnline: true,
            lastSeen: serverTimestamp(),
          });
          await updateDoc(userFirestoreRef, {
            isOnline: true,
            lastSeen: new Date().toISOString()
          });
        } catch (error) {
          if (!isPermissionDeniedError(error)) {
            console.error('Error updating presence:', error);
          }
        }
      }, 60000); // Update every minute

      return () => {
        cancelled = true;
        if (presenceInterval) clearInterval(presenceInterval);
        if (connectedUnsubscribe) connectedUnsubscribe();
      };
      
    } catch (error) {
      console.error('Error setting up presence:', error);
      return () => {
        cancelled = true;
        if (presenceInterval) clearInterval(presenceInterval);
        if (connectedUnsubscribe) connectedUnsubscribe();
      };
    }
  };

  const setupUserData = async (currentUser: User) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));

      // Firebase Auth is the source of truth for account creation time.
      // Normalize to ISO for storage/display.
      const authCreatedAtIso = (() => {
        const v = currentUser.metadata?.creationTime;
        if (!v) return new Date().toISOString();
        const d = new Date(v);
        return Number.isFinite(d.getTime()) ? d.toISOString() : new Date().toISOString();
      })();
      
      if (!userDoc.exists()) {
        // Create new user document if it doesn't exist
        const username = currentUser.email?.split('@')[0] || '';
        const newUserData: UserData = {
          id: currentUser.uid,
          name: currentUser.displayName || username,
          email: currentUser.email || '',
          photoURL: currentUser.photoURL,
          coverURL: null,
          username,
          bio: '',
          location: '',
          createdAt: authCreatedAtIso,
          lastSeen: new Date().toISOString(),
          isOnline: true,
          // Admin and role fields
          isAdmin: false,
          isSeller: false,
          role: 'user' as 'user' | 'admin' | 'seller' | 'superadmin',
          permissions: [],
        };
        
        await setDoc(doc(db, 'users', currentUser.uid), newUserData);
      } else {
        // Backfill missing createdAt (and don't overwrite if already present)
        const existing = userDoc.data() as Partial<UserData> | undefined;
        if (!existing?.createdAt) {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            createdAt: authCreatedAtIso,
          });
        }
        // userData is synced by onSnapshot listener.
      }
    } catch (error) {
      console.error('Error setting up user data:', error);
      throw error;
    }
  };

  const retryConnection = async () => {
    if (!navigator.onLine) {
      toast({
        title: 'Still offline',
        description: 'Please check your internet connection',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (user) {
        await setupUserData(user);
      }
      toast({
        title: 'Connected',
        description: 'You are back online',
      });
    } catch (error) {
      toast({
        title: 'Connection failed',
        description: 'Failed to reconnect to the server',
        variant: 'destructive',
      });
    }
  };

  const propagateProfilePhoto = async (uid: string, photoURL: string | null) => {
    const timestamp = new Date().toISOString();

    const updateCollectionDocs = async (collectionName: string, ownerField: string, fieldUpdates: Record<string, unknown>) => {
      const snapshot = await getDocs(query(collection(db, collectionName), where(ownerField, '==', uid)));
      if (snapshot.empty) return;

      let batch = writeBatch(db);
      let operationCount = 0;

      for (const snapshotDoc of snapshot.docs) {
        batch.update(snapshotDoc.ref, {
          ...fieldUpdates,
          updatedAt: timestamp,
        });
        operationCount += 1;

        if (operationCount === 450) {
          await batch.commit();
          batch = writeBatch(db);
          operationCount = 0;
        }
      }

      if (operationCount > 0) {
        await batch.commit();
      }
    };

    await Promise.allSettled([
      updateCollectionDocs('posts', 'authorId', { authorPhoto: photoURL }),
      updateCollectionDocs('messages', 'senderId', { senderPhoto: photoURL }),
      updateCollectionDocs('stories', 'userId', { userPhoto: photoURL, authorPhoto: photoURL, avatar: photoURL }),
    ]);
  };

  const uploadPhoto = async (file: File, type: 'profile' | 'cover'): Promise<string> => {
    if (!user) throw new Error('No authenticated user');

    try {
      // Get current user from auth to ensure we have the latest instance
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      // Upload file to storage
      const fileRef = storageRef(storage, `users/${currentUser.uid}/${type}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);

      // Update Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      const updateData = type === 'profile' 
        ? { photoURL: downloadURL }
        : { coverURL: downloadURL };
        
      await updateDoc(userRef, {
        ...updateData,
        updatedAt: new Date().toISOString(),
      });

      // Update Auth profile if it's a profile photo
      if (type === 'profile') {
        await updateProfile(currentUser, { photoURL: downloadURL });
        await propagateProfilePhoto(currentUser.uid, downloadURL);
      }

      // Update local state
      setUserData(prev => prev ? { ...prev, ...updateData } : null);

      return downloadURL;
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      throw new Error(error.message || 'Failed to upload photo');
    }
  };

  const updateUserData = async (data: Partial<UserData>) => {
    if (!user) throw new Error('No authenticated user');

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const userRef = doc(db, 'users', currentUser.uid);
      const updateData = {
        ...data,
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(userRef, updateData);

      if (data.name) {
        await updateProfile(currentUser, { displayName: data.name });
      }

      setUserData(prev => prev ? { ...prev, ...data } : null);

      toast({
        title: 'Success',
        description: 'Profile updated successfully',
      });
    } catch (error: any) {
      console.error('Error updating user data:', error);
      throw new Error(error.message || 'Failed to update profile');
    }
  };

  useEffect(() => {
    let presenceCleanup: undefined | (() => void);
    let userDocCleanup: undefined | (() => void);

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      // Auth state arrived: unblock UI immediately.
      setUser(currentUser);
      setLoading(false);

      // Clean up any previous presence interval when user changes.
      if (presenceCleanup) {
        presenceCleanup();
        presenceCleanup = undefined;
      }
      if (userDocCleanup) {
        userDocCleanup();
        userDocCleanup = undefined;
      }

      if (!currentUser) {
        setUserData(null);
        if (import.meta.env.DEV) console.log('User signed out');
        return;
      }

      // Keep user data in sync app-wide (e.g., photoURL updates from profile page).
      const userRef = doc(db, 'users', currentUser.uid);
      userDocCleanup = onSnapshot(userRef, (snapshot) => {
        if (!snapshot.exists()) return;

        const liveUserData = {
          id: snapshot.id,
          ...snapshot.data(),
        } as UserData;

        if (isMountedRef.current) {
          setUserData(liveUserData);
        }

        // Keep Firebase Auth profile aligned for consumers that still read user.photoURL/displayName.
        void (async () => {
          try {
            const authUser = auth.currentUser;
            if (!authUser || authUser.uid !== currentUser.uid) return;

            const profileUpdates: { displayName?: string | null; photoURL?: string | null } = {};

            if (typeof liveUserData.name === 'string' && liveUserData.name !== authUser.displayName) {
              profileUpdates.displayName = liveUserData.name;
            }

            const authPhoto = authUser.photoURL ?? null;
            const livePhoto = liveUserData.photoURL ?? null;
            if (livePhoto !== authPhoto) {
              profileUpdates.photoURL = livePhoto;
            }

            if (Object.keys(profileUpdates).length > 0) {
              await updateProfile(authUser, profileUpdates);
            }
          } catch (syncError) {
            console.error('Error syncing auth profile from user data:', syncError);
          }
        })();
      }, (error) => {
        if (isPermissionDeniedError(error)) {
          // Avoid noisy retry logs when this project/environment denies user profile reads.
          if (import.meta.env.DEV) {
            console.warn('Permission denied while listening to user profile; using auth fallback state.');
          }
          if (isMountedRef.current) {
            setUserData((prev) => prev ?? {
              id: currentUser.uid,
              name: currentUser.displayName || '',
              email: currentUser.email || '',
              photoURL: currentUser.photoURL,
              coverURL: null,
              username: '',
              bio: '',
              location: '',
              createdAt: new Date().toISOString(),
              lastSeen: new Date().toISOString(),
              isOnline: navigator.onLine,
              isAdmin: false,
              isSeller: false,
              role: 'user',
              permissions: [],
            });
          }
          return;
        }
        console.error('Error listening to user data updates:', error);
      });

      // Background initialization (do NOT block first paint)
      (async () => {
        try {
          await recordSuccessfulLogin(currentUser);
          await setupUserData(currentUser);

          // setupPresence returns a cleanup in the success path.
          presenceCleanup = await setupPresence(currentUser);

          if (import.meta.env.DEV) console.log('User signed in:', currentUser.email);
        } catch (error) {
          console.error('Error setting up user:', error);

          // Only show critical errors
          if (error instanceof Error && error.message.includes('permission')) {
            toast({
              title: 'Error',
              description: 'Failed to load user data',
              variant: 'destructive',
            });
          }

          // If something failed, keep UI usable (don’t re-block). Just ensure state isn't updated after unmount.
          if (isMountedRef.current) {
            // no-op; placeholder for future fallback logic
          }
        }
      })();
    });

    return () => {
      unsubscribe();
      if (presenceCleanup) presenceCleanup();
      if (userDocCleanup) userDocCleanup();
    };
  }, []); // Remove toast and isOffline from dependencies

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const syncedUser = useMemo(() => {
    if (!user) return null;

    const resolvedPhotoURL = userData?.photoURL ?? user.photoURL ?? null;
    const resolvedDisplayName = userData?.name ?? user.displayName ?? null;

    if (resolvedPhotoURL === user.photoURL && resolvedDisplayName === user.displayName) {
      return user;
    }

    const userClone = Object.create(Object.getPrototypeOf(user));
    Object.assign(userClone, user, {
      photoURL: resolvedPhotoURL,
      displayName: resolvedDisplayName,
    });

    return userClone as User;
  }, [user, userData?.photoURL, userData?.name]);

  const value = {
    user: syncedUser,
    loading,
    userData,
    isOffline,
    updateUserData,
    uploadPhoto,
    retryConnection
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}